import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import Logo from "./Logo";

const SOLUTIONS = [
  { label: "Assicurazione Auto",  to: "/assicurazione-auto-milano",   testid: "nav-sol-auto" },
  { label: "Assicurazione Casa",  to: "/assicurazione-casa",          testid: "nav-sol-casa" },
  { label: "Polizza Vita",        to: "/polizza-vita",                testid: "nav-sol-vita" },
  { label: "Infortuni e Malattia", to: "/infortuni-malattia", testid: "nav-sol-infortuni" },
  { label: "RC Professionale",    to: "/rc-professionale",            testid: "nav-sol-rc-pro" },
  { label: "Monopattino Elettrico", to: "/assicurazione-monopattino", testid: "nav-sol-monopattino" },
  { label: "Cani e Gatti", to: "/assicurazione-cani-gatti", testid: "nav-sol-cani-gatti" },
];

export default function Header() {
  const [solOpen, setSolOpen]   = useState(false); // desktop dropdown
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSolOpen, setMobileSolOpen] = useState(true);
  const { pathname } = useLocation();

  // Close menus on route change
  useEffect(() => {
    setSolOpen(false);
    setMobileOpen(false);
  }, [pathname]);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [mobileOpen]);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
    }`;

  const isSolActive = SOLUTIONS.some((s) => s.to === pathname);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#FAF9F6]/80 backdrop-blur-xl border-b border-slate-200/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-link">
          <Logo className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 shrink-0 text-slate-900" />
          <div className="leading-none min-w-0">
            <div className="font-serif text-sm sm:text-xl md:text-2xl lg:text-3xl text-slate-900 uppercase tracking-wide leading-none">DA PRA ASSICURAZIONI</div>
            <div className="hidden sm:block overline mt-1.5 text-[10px] md:text-[11px] whitespace-nowrap">Agenzia · Plurimandataria · Dal 1958</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>

          {/* Soluzioni dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setSolOpen(true)}
            onMouseLeave={() => setSolOpen(false)}
          >
            <button
              type="button"
              data-testid="nav-soluzioni"
              aria-haspopup="true"
              aria-expanded={solOpen}
              onClick={() => setSolOpen((v) => !v)}
              className={`inline-flex items-center gap-1 text-sm tracking-wide transition-colors ${isSolActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"}`}
            >
              Soluzioni
              <ChevronDown size={14} className={`transition-transform duration-200 ${solOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Dropdown panel */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[260px] transition-all duration-200 ${
                solOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-1 pointer-events-none"
              }`}
              role="menu"
            >
              <div className="bg-white border border-slate-200 shadow-xl">
                <div className="px-5 pt-5 pb-3">
                  <div className="overline text-[10px]">Le nostre polizze</div>
                </div>
                <ul className="pb-3">
                  {SOLUTIONS.map((s) => (
                    <li key={s.to}>
                      <NavLink
                        to={s.to}
                        data-testid={s.testid}
                        className={({ isActive }) =>
                          `block px-5 py-2.5 text-sm transition-colors border-l-2 ${
                            isActive
                              ? "border-[#C5A059] text-slate-900 bg-[#FAF9F6]"
                              : "border-transparent text-slate-700 hover:border-[#C5A059] hover:text-slate-900 hover:bg-[#FAF9F6]"
                          }`
                        }
                      >
                        {s.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <a href="/#chi-siamo" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-chi-siamo">Chi siamo</a>
          <a href="/#compliance" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-compliance">Compliance</a>
          <a href="/#contatti" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-contatti">Contatti</a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/preventivo"
            data-testid="header-cta-preventivo"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-xs sm:text-sm tracking-wide px-3 sm:px-5 h-9 sm:h-11 transition-colors"
          >
            <span className="hidden sm:inline">Richiedi preventivo</span>
            <span className="sm:hidden">Preventivo</span>
          </Link>

          {/* Mobile burger */}
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label="Apri menu"
            onClick={() => setMobileOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9 text-slate-900 border border-slate-300"
          >
            <Menu size={18} />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay — rendered via Portal to escape header's backdrop-blur containing block */}
      {mobileOpen && createPortal(
        <div
          data-testid="mobile-menu"
          className="md:hidden fixed inset-0 z-[100] bg-[#FAF9F6] flex flex-col"
        >
          <div className="h-16 px-4 flex items-center justify-between border-b border-slate-200">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
              <Logo className="w-8 h-8 text-slate-900" />
              <span className="font-serif text-base text-slate-900 uppercase tracking-wide">Da Pra Assicurazioni</span>
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-close"
              aria-label="Chiudi menu"
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 inline-flex items-center justify-center text-slate-900 border border-slate-300"
            >
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-8 space-y-1">
            <NavLink
              to="/"
              end
              onClick={() => setMobileOpen(false)}
              className="block py-4 text-2xl font-serif text-slate-900 border-b border-slate-200"
              data-testid="mnav-home"
            >
              Home
            </NavLink>

            <div className="border-b border-slate-200">
              <button
                type="button"
                onClick={() => setMobileSolOpen((v) => !v)}
                data-testid="mnav-soluzioni"
                className="w-full flex items-center justify-between py-4 text-2xl font-serif text-slate-900"
                aria-expanded={mobileSolOpen}
              >
                Soluzioni
                <ChevronDown size={18} className={`transition-transform ${mobileSolOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileSolOpen && (
                <ul className="pb-4 pl-2 space-y-1">
                  {SOLUTIONS.map((s) => (
                    <li key={s.to}>
                      <NavLink
                        to={s.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`m${s.testid}`}
                        className={({ isActive }) =>
                          `block py-2.5 pl-4 text-base border-l-2 transition-colors ${
                            isActive ? "border-[#C5A059] text-slate-900" : "border-slate-200 text-slate-600 hover:text-slate-900"
                          }`
                        }
                      >
                        {s.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <a
              href="/#chi-siamo"
              onClick={() => setMobileOpen(false)}
              data-testid="mnav-chi-siamo"
              className="block py-4 text-2xl font-serif text-slate-900 border-b border-slate-200"
            >Chi siamo</a>
            <a
              href="/#compliance"
              onClick={() => setMobileOpen(false)}
              data-testid="mnav-compliance"
              className="block py-4 text-2xl font-serif text-slate-900 border-b border-slate-200"
            >Compliance</a>
            <a
              href="/#contatti"
              onClick={() => setMobileOpen(false)}
              data-testid="mnav-contatti"
              className="block py-4 text-2xl font-serif text-slate-900 border-b border-slate-200"
            >Contatti</a>
          </nav>

          <div className="p-6 border-t border-slate-200">
            <Link
              to="/preventivo"
              onClick={() => setMobileOpen(false)}
              data-testid="mnav-cta"
              className="block w-full text-center bg-slate-900 hover:bg-slate-800 text-white text-sm tracking-wide h-12 leading-[3rem]"
            >
              Richiedi preventivo
            </Link>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
